import { useEffect, useState, useCallback } from "react";
import { osApi, formatINR } from "./osApi";
import { toast } from "sonner";
import { Banknote } from "lucide-react";

const STAGES = ["Login", "Docs", "Approved", "Disbursed"];
const STAGE_COLORS = { Login: "os-badge-blue", Docs: "os-badge-amber", Approved: "os-badge-green", Disbursed: "os-badge-green" };

export default function OSLoans() {
  const [items, setItems] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [stage, setStage] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await osApi.listLoans({ stage: stage || undefined, q: q || undefined });
      setItems(res.items); setStageCounts(res.stage_counts || {});
    } catch { toast.error("Failed"); } finally { setLoading(false); }
  }, [stage, q]);
  useEffect(() => { load(); }, [load]);

  const updateStage = async (id, newStage) => {
    try { await osApi.updateLoan(id, { stage: newStage }); toast.success(`Moved to ${newStage}`); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="os-loans-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Loan Management</h1>
        <p className="text-sm mt-1" style={{color:"var(--os-text-muted)"}}>Login → Docs → Approved → Disbursed. Track every loan application end-to-end.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {STAGES.map(s => (
          <div key={s} className="os-metric" data-testid={`os-loan-stage-${s.toLowerCase()}`}>
            <div className="os-metric-label">{s}</div>
            <div className="os-metric-value os-mono">{stageCounts[s] ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="os-card p-3 flex gap-2 flex-wrap">
        <input className="os-input flex-1 min-w-[180px]" placeholder="Search customer / bank" value={q} onChange={e => setQ(e.target.value)} data-testid="os-loan-search" />
        <select className="os-input max-w-[140px]" value={stage} onChange={e => setStage(e.target.value)} data-testid="os-loan-filter-stage">
          <option value="">All stages</option>
          {STAGES.map(s => <option key={s}>{s}</option>)}
        </select>
      </div>

      <div className="os-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="os-table">
            <thead><tr><th>Customer</th><th>Type</th><th>Bank</th><th>Amount</th><th>Interest</th><th>Tenure</th><th>Stage</th><th>Action</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-10" style={{color:"var(--os-text-muted)"}}>Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={8} className="text-center py-10" style={{color:"var(--os-text-muted)"}}>No loan applications.</td></tr>}
              {items.map(l => (
                <tr key={l.id} data-testid={`os-loan-row-${l.id}`}>
                  <td className="font-medium">{l.customer_name}</td>
                  <td><span className="os-badge os-badge-blue">{l.loan_type}</span></td>
                  <td>{l.bank || "—"}</td>
                  <td className="os-mono">{formatINR(l.amount)}</td>
                  <td className="os-mono">{l.interest_rate ? `${l.interest_rate}%` : "—"}</td>
                  <td>{l.tenure_years ? `${l.tenure_years}y` : "—"}</td>
                  <td><span className={`os-badge ${STAGE_COLORS[l.stage]}`}>{l.stage}</span></td>
                  <td>
                    <select className="os-input py-1 text-xs max-w-[120px]" value={l.stage} onChange={e => updateStage(l.id, e.target.value)} data-testid={`os-loan-move-${l.id}`}>
                      {STAGES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
